import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Icon } from "@/lib/icons";
import { Search, Loader2, BookA, Building2, GraduationCap, BookMarked, Library } from "lucide-react";

export default function SearchResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initial = params.get("q") || "";
  const [q, setQ] = useState(initial);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = (query) => {
    if (!query.trim()) { setResults(null); return; }
    setLoading(true);
    api.get(`/search?q=${encodeURIComponent(query)}`).then((r) => setResults(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { if (initial) run(initial); /* eslint-disable-next-line */ }, [initial]);

  const total = results ? Object.values(results).reduce((a, b) => a + b.length, 0) : 0;

  return (
    <div className="space-y-8" data-testid="search-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Global Search</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Search everything in ECO.</h1>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); navigate(`/search?q=${encodeURIComponent(q)}`); run(q); }} className="relative max-w-2xl">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
        <input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Topics, terms, companies, skills…" className="eco-input pl-12 py-4 text-lg" />
      </form>

      {loading && <div className="flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Searching…</div>}

      {results && !loading && (
        <div className="space-y-8">
          {total === 0 && <p className="text-slate-500">No results found. Try the ECO Mentor for an instant answer.</p>}

          <Section title="Dictionary terms" icon={BookA} show={results.terms.length}>
            {results.terms.map((t) => (
              <Link key={t.term} to="/dictionary" className="eco-card eco-card-hover p-4" data-testid={`search-term-${t.term}`}>
                <div className="font-display font-semibold text-white">{t.term} <span className="text-xs text-slate-500 font-normal">{t.full}</span></div>
                <p className="text-sm text-slate-400 mt-1 line-clamp-2">{t.definition}</p>
              </Link>
            ))}
          </Section>

          <Section title="Categories" icon={Library} show={results.categories.length}>
            {results.categories.map((c) => (
              <Link key={c.id} to={`/knowledge/${c.id}`} className="eco-card eco-card-hover p-4 flex items-center gap-3" data-testid={`search-cat-${c.id}`}>
                <Icon name={c.icon} size={20} style={{ color: c.color }} />
                <div><div className="font-medium text-white">{c.name}</div><div className="text-xs text-slate-500 line-clamp-1">{c.description}</div></div>
              </Link>
            ))}
          </Section>

          <Section title="Case studies" icon={Building2} show={results.case_studies.length}>
            {results.case_studies.map((s) => (
              <Link key={s.id} to={`/case-studies/${s.id}`} className="eco-card eco-card-hover p-4" data-testid={`search-study-${s.id}`}>
                <div className="font-display font-semibold text-white">{s.company}</div>
                <p className="text-sm text-slate-400 mt-1">{s.tagline}</p>
              </Link>
            ))}
          </Section>

          <Section title="Learning paths" icon={GraduationCap} show={results.paths.length}>
            {results.paths.map((p) => (
              <Link key={p.id} to={`/university/${p.id}`} className="eco-card eco-card-hover p-4 flex items-center gap-3" data-testid={`search-path-${p.id}`}>
                <Icon name={p.icon} size={20} className="text-[#4d8bff]" />
                <div><div className="font-medium text-white">{p.title}</div><div className="text-xs text-slate-500 line-clamp-1">{p.description}</div></div>
              </Link>
            ))}
          </Section>

          <Section title="Resources" icon={BookMarked} show={results.resources.length}>
            {results.resources.map((r) => (
              <a key={r.name} href={r.url} target="_blank" rel="noopener noreferrer" className="eco-card eco-card-hover p-4" data-testid={`search-res-${r.name}`}>
                <div className="font-medium text-white">{r.name}</div>
                <p className="text-sm text-slate-400 mt-1 line-clamp-2">{r.description}</p>
              </a>
            ))}
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: I, show, children }) {
  if (!show) return null;
  return (
    <div>
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-white mb-3"><I size={18} className="text-slate-500" /> {title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}
